import React, { useState, useEffect, useCallback } from 'react';
import type { Student, AIAnalysisResult, ClassRoom } from '../../types/db';
import { UserRole } from '../../types/db';
import {
  FileSignature,
  Sparkles,
  Loader2,
  Printer,
  CheckCircle,
  AlertCircle,
  Save,
  X,
  Calendar,
  BookOpen,
  GraduationCap,
  History,
  RefreshCw,
  Layers,
  CheckCircle2,
  HelpCircle,
  Filter,
} from 'lucide-react';
import { analyzeStudentPerformance } from '../../services/geminiService';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';

// Helper agar format tanggal di UI selalu Indonesia (DD/MM/YYYY)
const formatDateID = (dateString: string) => {
  if (!dateString) return '-';
  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

export function ReportManager({ students }: { students: Student[] }) {
  const { profile: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'DAILY' | 'HISTORY_FILTER' | 'PERIODIC'>('DAILY');
  
  // State Filter & Kelas
  const [classes, setClasses] = useState<ClassRoom[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [selectedId, setSelectedId] = useState(''); // Untuk Analisis Periodik AI
  
  // State Filter Rentang Tanggal Riwayat
  const [historyStudentId, setHistoryStudentId] = useState<string>('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [filteredHistoryReports, setFilteredHistoryReports] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [fetchingReports, setFetchingReports] = useState(false);

  const isTentor = currentUser?.role === UserRole.TENTOR;
  const isAdmin =
    currentUser &&
    [
      UserRole.MASTER_ADMIN,
      UserRole.SUPER_ADMIN,
      UserRole.ACADEMIC,
      UserRole.CENTRAL_ADMIN,
    ].includes(currentUser.role);

  const [dailyReports, setDailyReports] = useState<any[]>([]);
  const [sessionExamData, setSessionExamData] = useState<any>(null);
  const [studentQuizScores, setStudentQuizScores] = useState<Record<string, number>>({});

  // State Form Harian Berbasis Kelas
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0]);
  const [subject, setSubject] = useState('');
  const [classFormRecords, setClassFormRecords] = useState<
    Record<string, { attendance: 'H' | 'I' | 'S' | 'A'; comprehension_score: number; quiz_score: number; notes: string }>
  >({});

  const [periodicReport, setPeriodicReport] = useState<AIAnalysisResult | null>(null);

  // 1. Fetch Daftar Kelas Cabang
  const fetchClasses = useCallback(async () => {
    let query = supabase.from('classes').select('*').order('name');
    if (currentUser?.branch_id && currentUser.branch_id !== 'HEADQUARTERS' && currentUser.branch_id !== 'ALL') {
      query = query.eq('branch_id', currentUser.branch_id);
    }
    const { data } = await query;
    if (data && data.length > 0) {
      setClasses(data);
      setSelectedClassId((prev) => (prev ? prev : data[0].id));
    } else {
      setClasses([]);
      setSelectedClassId('');
    }
  }, [currentUser]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const filteredStudents = students.filter(
    (s) => s.class_id === selectedClassId && s.is_active !== false
  );

  // 2. Cek apakah ada Jadwal & Kuis yang terikat pada Tanggal & Kelas ini
  useEffect(() => {
    let isMounted = true;
    const fetchSessionExam = async () => {
      if (!selectedClassId || !sessionDate) return;
      try {
        const { data: scheduleData } = await supabase
          .from('schedules')
          .select('*, exam:exams(*)')
          .eq('class_id', selectedClassId)
          .eq('date', sessionDate)
          .maybeSingle();

        if (!isMounted) return;

        if (scheduleData && scheduleData.exam) {
          setSessionExamData(scheduleData.exam);
          if (scheduleData.subject) {
            setSubject((prev) => (prev ? prev : scheduleData.subject));
          }

          const { data: resultsData } = await supabase
            .from('exam_results')
            .select('*')
            .eq('exam_id', scheduleData.exam_id);

          if (!isMounted) return;

          if (resultsData) {
            const scoreMap: Record<string, number> = {};
            resultsData.forEach((res) => {
              scoreMap[res.student_id] = res.score;
            });
            setStudentQuizScores(scoreMap);
          }
        } else {
          setSessionExamData(null);
          setStudentQuizScores({});
        }
      } catch (err) {
        /* handled silently */
      }
    };

    fetchSessionExam();
    return () => {
      isMounted = false;
    };
  }, [selectedClassId, sessionDate]);

  useEffect(() => {
    if (!selectedClassId) return;

    setClassFormRecords((prevRecords) => {
      const initialRecords: Record<
        string,
        { attendance: 'H' | 'I' | 'S' | 'A'; comprehension_score: number; quiz_score: number; notes: string }
      > = {};

      filteredStudents.forEach((s) => {
        const autoQuizScore = studentQuizScores[s.id] !== undefined ? studentQuizScores[s.id] : 100;
        
        initialRecords[s.id] = prevRecords[s.id] || {
          attendance: 'H',
          comprehension_score: 8,
          quiz_score: autoQuizScore,
          notes: '',
        };
      });

      return initialRecords;
    });
  }, [selectedClassId, filteredStudents, studentQuizScores]);

  const handleMarkAllPresent = () => {
    const updated = { ...classFormRecords };
    filteredStudents.forEach((s) => {
      updated[s.id] = {
        ...(updated[s.id] || { comprehension_score: 8, quiz_score: 100, notes: '' }),
        attendance: 'H',
      };
    });
    setClassFormRecords(updated);
  };

  const fetchDailyReports = useCallback(async () => {
    setFetchingReports(true);
    try {
      let query = supabase
        .from('daily_reports')
        .select(
          `
          *,
          students ( id, name, branch_id ),
          tentor:profiles!daily_reports_tentor_id_fkey ( id, name )
        `
        )
        .order('date', { ascending: false })
        .limit(100);

      if (isTentor && !isAdmin) {
        query = query.eq('tentor_id', currentUser?.id);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Filter branch dinonaktifkan agar tidak terjadi konflik data kosong akibat perbedaan format cabang
      setDailyReports(data || []);
    } catch (err: any) {
      console.error("Gagal fetch daily reports:", err);
    } finally {
      setFetchingReports(false);
    }
  }, [currentUser, isTentor, isAdmin]);

  useEffect(() => {
    fetchDailyReports();
  }, [fetchDailyReports]);

  // Filter Riwayat Berdasarkan Siswa & Rentang Tanggal
  const handleFilterHistory = async () => {
    if (!historyStudentId) {
      return window.alert('Pilih siswa terlebih dahulu untuk memfilter laporan!');
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('daily_reports')
        .select(`*, students(id, name), tentor:profiles!daily_reports_tentor_id_fkey(name)`)
        .eq('student_id', historyStudentId)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });

      if (error) throw error;
      setFilteredHistoryReports(data || []);
    } catch (e: any) {
      window.alert('Gagal mengambil riwayat: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Fungsi Cetak / Download PDF Riwayat Siswa
  const printHistoryReport = () => {
    const content = document.getElementById('history-print-area');
    if (!content) return;
    const selectedStudentObj = students.find((s) => s.id === historyStudentId);
    const studentNameText = selectedStudentObj ? selectedStudentObj.name.toUpperCase() : 'SISWA';
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Riwayat Perkembangan - ${studentNameText}</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>body { font-family: sans-serif; padding: 30px; }</style>
        </head>
        <body>
          <div class="border-b-2 border-slate-900 pb-4 mb-6">
            <h1 class="text-xl font-black uppercase">REKAP RIWAYAT PERKEMBANGAN & KUIS SISWA</h1>
            <p class="text-xs text-slate-700 font-bold mt-1">Nama Siswa: ${studentNameText}</p>
            <p class="text-xs text-slate-500 mt-0.5">Periode: ${formatDateID(startDate)} s.d. ${formatDateID(endDate)}</p>
          </div>
          ${content.innerHTML}
          <div class="mt-8 flex justify-between text-xs font-bold text-slate-600">
            <div>Mengetahui, Tim Akademik</div>
            <div>Orang Tua / Wali Siswa</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    window.setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const saveBatchDailyReport = async () => {
    if (!currentUser?.id) {
      return window.alert('Gagal: Sesi Profile Anda tidak valid. Mohon login ulang.');
    }

    if (!subject) {
      return window.alert('Mohon isi Mata Pelajaran terlebih dahulu!');
    }

    if (filteredStudents.length === 0) {
      return window.alert('Tidak ada siswa di kelas ini.');
    }

    setLoading(true);
    try {
      const dailyPayload = filteredStudents.map((s) => {
        const rec = classFormRecords[s.id] || {
          attendance: 'H',
          comprehension_score: 8,
          quiz_score: 100,
          notes: '',
        };
        return {
          student_id: s.id,
          tentor_id: currentUser.id,
          date: sessionDate,
          subject: subject.toUpperCase(),
          attendance: rec.attendance,
          comprehension_score: rec.comprehension_score,
          quiz_score: rec.quiz_score,
          notes: rec.notes || 'Mengikuti pembelajaran dan kuis dengan baik.',
          created_at: new Date().toISOString(),
        };
      });

      const attendancePayload = filteredStudents.map((s) => {
        const rec = classFormRecords[s.id] || { attendance: 'H' };
        return {
          student_id: s.id,
          branch_id: s.branch_id,
          date: sessionDate,
          status: rec.attendance,
        };
      });

      const [dailyRes, attRes] = await Promise.all([
        supabase.from('daily_reports').insert(dailyPayload),
        supabase
          .from('attendance_records')
          .upsert(attendancePayload, { onConflict: 'student_id, date' }),
      ]);

      if (dailyRes.error) throw dailyRes.error;
      if (attRes.error) throw attRes.error;

      window.alert(`Laporan Harian, Presensi & Nilai Kuis Berhasil Disimpan! (${dailyPayload.length} Siswa)`);
      fetchDailyReports();
    } catch (e: any) {
      window.alert('Gagal menyimpan: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const printAcademicDailyReport = () => {
    const activeClass = classes.find((c) => c.id === selectedClassId);
    const classNameText = activeClass?.name || 'KELAS';
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Daily Report & Quiz - ${classNameText}</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>body { font-family: sans-serif; padding: 30px; }</style>
        </head>
        <body>
          <div class="border-b-2 border-slate-900 pb-4 mb-6 flex justify-between items-end">
            <div>
              <h1 class="text-xl font-black uppercase">REKAP DAILY REPORT & KUIS HARIAN SISWA</h1>
              <p class="text-xs text-slate-600 mt-1">Kelas: ${classNameText} | Mapel: ${subject || '-'} | Tanggal: ${formatDateID(sessionDate)}</p>
            </div>
            <div class="text-right text-xs font-bold text-slate-500">Bimbel System</div>
          </div>
          <table class="w-full text-left border-collapse border border-slate-300 text-xs">
            <thead>
              <tr class="bg-slate-100 uppercase font-black text-slate-700">
                <th class="p-2 border text-center w-10">No</th>
                <th class="p-2 border">Nama Siswa</th>
                <th class="p-2 border text-center w-20">Absen</th>
                <th class="p-2 border text-center w-28">Nilai Kuis (0-100)</th>
                <th class="p-2 border text-center w-28">Skor Pemahaman</th>
                <th class="p-2 border">Catatan Tentor</th>
              </tr>
            </thead>
            <tbody>
              ${filteredStudents
                .map((s, idx) => {
                  const rec = classFormRecords[s.id] || { attendance: 'H', comprehension_score: 8, quiz_score: 100, notes: '' };
                  return `
                  <tr class="border">
                    <td class="p-2 border text-center font-bold">${idx + 1}</td>
                    <td class="p-2 border font-bold uppercase">${s.name}</td>
                    <td class="p-2 border text-center font-bold">${rec.attendance}</td>
                    <td class="p-2 border text-center font-bold">${rec.quiz_score}</td>
                    <td class="p-2 border text-center font-bold">${rec.comprehension_score}/10</td>
                    <td class="p-2 border italic">${rec.notes || '-'}</td>
                  </tr>
                `;
                })
                .join('')}
            </tbody>
          </table>
          <div class="mt-8 flex justify-between text-xs font-bold text-slate-600">
            <div>Mengetahui, Tim Akademik</div>
            <div>Tentor Pengampu</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    window.setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleAnalyzePeriodic = async () => {
    if (isTentor && !isAdmin)
      return window.alert('Otoritas Ditolak: Fitur Analisis Periodik hanya untuk Admin/Akademik.');

    const s = students.find((x) => x.id === selectedId);
    if (!s) return window.alert('Pilih siswa terlebih dahulu!');

    setLoading(true);
    try {
      const res = await analyzeStudentPerformance(s);
      setPeriodicReport(res);
      window.alert('Analisis Periodik Berhasil! Rekomendasi AI telah disematkan.');
    } catch (e: any) {
      window.alert('Gagal menganalisis: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const printPeriodicReport = () => {
    const content = document.getElementById('rapor-periodic-print');
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Rapor Digital AI - Delta</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>body { font-family: sans-serif; padding: 40px; }</style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    window.setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 750);
  };

  return (
    <div className="space-y-8 animate-fade-in font-sans pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-100">
            <FileSignature className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight">
              Rapor Digital Delta
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
              Sistem Evaluasi Berkelanjutan Siswa & Daily Quiz
            </p>
          </div>
        </div>

        <div className="flex bg-white p-1.5 rounded-2xl border border-slate-100 shadow-sm w-full md:w-auto">
          <button
            onClick={() => setActiveTab('DAILY')}
            className={`flex-1 md:flex-none px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeTab === 'DAILY' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-indigo-600'}`}
          >
            Laporan Harian
          </button>
          <button
            onClick={() => setActiveTab('HISTORY_FILTER')}
            className={`flex-1 md:flex-none px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeTab === 'HISTORY_FILTER' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-indigo-600'}`}
          >
            Filter Riwayat Siswa
          </button>
          <button
            onClick={() => setActiveTab('PERIODIC')}
            className={`flex-1 md:flex-none px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeTab === 'PERIODIC' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-indigo-600'}`}
          >
            Analisis AI
          </button>
        </div>
      </div>

      <div className="bg-white p-8 md:p-10 rounded-[3rem] border border-slate-50 shadow-sm min-h-[600px]">
        {activeTab === 'DAILY' ? (
          <div className="space-y-10">
            {/* BAR FILTER KELAS & MAPEL */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-slate-50 pb-8">
              <div className="space-y-1">
                <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5" /> Pilih Kelas
                </label>
                <select
                  className="w-full border-2 border-slate-200 p-4 rounded-2xl text-xs font-black uppercase bg-white text-slate-900 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all cursor-pointer"
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                >
                  {classes.map((c) => (
                    <option key={c.id} value={c.id} className="text-slate-900 font-bold">
                      {c.name}
                    </option>
                  ))}
                  {classes.length === 0 && (
                    <option value="" className="text-slate-400">
                      Belum ada kelas di unit ini
                    </option>
                  )}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" /> Tanggal Sesi ({formatDateID(sessionDate)})
                </label>
                <input
                  type="date"
                  className="w-full border-2 border-slate-200 p-4 rounded-2xl text-xs font-black bg-white text-slate-900 outline-none"
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                />
              </div>

              <div className="space-y-1">
                <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                  <BookOpen className="w-3.5 h-3.5" /> Mata Pelajaran
                </label>
                <input
                  className="w-full border-2 border-slate-200 p-4 rounded-2xl text-xs font-black uppercase bg-white text-slate-900 outline-none placeholder:text-slate-300"
                  placeholder="MISAL: MATEMATIKA"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value.toUpperCase())}
                />
              </div>
            </div>

            {sessionExamData && (
              <div className="bg-indigo-50 p-4 rounded-2xl border border-indigo-100 flex items-center gap-3 text-xs font-bold text-indigo-900">
                <HelpCircle className="w-5 h-5 text-indigo-600 shrink-0" />
                <div>
                  <span className="uppercase font-black">Paket Kuis Tertaut:</span> {sessionExamData.subject} - {sessionExamData.topic || 'Kuis Harian'} ({sessionExamData.questions?.length || 5} Soal). Nilai siswa di bawah otomatis ditarik dari hasil pengerjaan kuis.
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-4">
                <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-2">
                  <h4 className="font-black text-slate-800 uppercase text-xs tracking-widest flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-indigo-600" /> Input Absen, Kuis & Progres ({filteredStudents.length} Siswa)
                  </h4>
                  <div className="flex items-center gap-2">
                    {filteredStudents.length > 0 && (
                      <>
                        <button
                          onClick={handleMarkAllPresent}
                          className="text-[9px] font-black uppercase tracking-wider text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-xl border border-emerald-200 transition-all flex items-center gap-1.5"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Semua Hadir
                        </button>
                        <button
                          onClick={printAcademicDailyReport}
                          className="text-[9px] font-black uppercase tracking-wider text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-xl border border-slate-200 transition-all flex items-center gap-1.5"
                          title="Cetak Laporan PDF untuk Akademik"
                        >
                          <Printer className="w-3.5 h-3.5" /> Unduh PDF Akademik
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {filteredStudents.length > 0 ? (
                  <div className="space-y-4">
                    {filteredStudents.map((s) => {
                      const rec = classFormRecords[s.id] || {
                        attendance: 'H',
                        comprehension_score: 8,
                        quiz_score: 100,
                        notes: '',
                      };
                      const hasAutoScore = studentQuizScores[s.id] !== undefined;
                      return (
                        <div
                          key={s.id}
                          className="bg-slate-50 p-6 rounded-3xl border border-slate-100 space-y-4 hover:bg-white hover:shadow-md transition-all"
                        >
                          <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center font-black text-xs shadow-md">
                                {s.name.charAt(0)}
                              </div>
                              <div>
                                <p className="font-black text-slate-800 uppercase text-xs">{s.name}</p>
                                <p className="text-[8px] text-slate-400 font-bold uppercase mt-0.5">
                                  {s.program_choice || 'REGULER'}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-1.5">
                              {[
                                { k: 'H', l: 'HADIR', c: 'bg-emerald-500 text-white' },
                                { k: 'I', l: 'IZIN', c: 'bg-amber-500 text-white' },
                                { k: 'S', l: 'SAKIT', c: 'bg-blue-500 text-white' },
                                { k: 'A', l: 'ALFA', c: 'bg-rose-500 text-white' },
                              ].map((status) => (
                                <button
                                  key={status.k}
                                  type="button"
                                  onClick={() =>
                                    setClassFormRecords({
                                      ...classFormRecords,
                                      [s.id]: { ...rec, attendance: status.k as any },
                                    })
                                  }
                                  className={`px-3 py-1.5 rounded-xl text-[9px] font-black transition-all border ${
                                    rec.attendance === status.k
                                      ? `${status.c} border-transparent shadow-sm`
                                      : 'bg-white border-slate-200 text-slate-400 hover:border-indigo-300'
                                  }`}
                                >
                                  {status.k}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-200/60 items-center">
                            <div className="flex items-center justify-between bg-white p-3 rounded-2xl border border-slate-200/60">
                              <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1.5">
                                Kuis (0-100): {hasAutoScore && <span className="bg-emerald-100 text-emerald-700 text-[7px] px-1.5 py-0.5 rounded">Otomatis</span>}
                              </span>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                className="w-20 border border-slate-200 rounded-xl px-3 py-1 text-xs font-black bg-slate-50 text-indigo-700 outline-none text-center"
                                value={rec.quiz_score}
                                onChange={(e) =>
                                  setClassFormRecords({
                                    ...classFormRecords,
                                    [s.id]: { ...rec, quiz_score: Number(e.target.value) },
                                  })
                                }
                              />
                            </div>

                            <div className="flex items-center justify-between bg-white p-3 rounded-2xl border border-slate-200/60">
                              <span className="text-[9px] font-black text-indigo-600 uppercase tracking-widest">
                                Pemahaman: {rec.comprehension_score}/10
                              </span>
                              <input
                                type="range"
                                min="1"
                                max="10"
                                className="w-28 h-2 bg-slate-200 rounded-lg cursor-pointer accent-indigo-600"
                                value={rec.comprehension_score}
                                onChange={(e) =>
                                  setClassFormRecords({
                                    ...classFormRecords,
                                    [s.id]: { ...rec, comprehension_score: Number(e.target.value) },
                                  })
                                }
                              />
                            </div>
                          </div>

                          <textarea
                            className="w-full border border-slate-200 p-3 rounded-xl font-medium bg-white outline-none h-16 resize-none text-xs leading-relaxed focus:border-indigo-400"
                            placeholder="Catatan progres atau kendala siswa..."
                            value={rec.notes}
                            onChange={(e) =>
                              setClassFormRecords({
                                ...classFormRecords,
                                [s.id]: { ...rec, notes: e.target.value },
                              })
                            }
                          />
                        </div>
                      );
                    })}

                    <button
                      onClick={saveBatchDailyReport}
                      disabled={loading}
                      className="w-full bg-indigo-600 text-white py-5 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl flex items-center justify-center gap-3 hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50 mt-6"
                    >
                      {loading ? <Loader2 className="animate-spin w-5 h-5" /> : <Save className="w-5 h-5" />}
                      SIMPAN LAPORAN HARIAN & KUIS KELAS
                    </button>
                  </div>
                ) : (
                  <div className="py-20 text-center flex flex-col items-center justify-center bg-slate-50 rounded-3xl border-2 border-dashed border-slate-100">
                    <Layers className="w-12 h-12 text-slate-200 mb-2" />
                    <p className="text-slate-400 font-black uppercase text-xs">
                      {classes.length === 0 ? 'Belum ada kelas di unit ini' : 'Belum ada siswa terdaftar di kelas ini'}
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-slate-50/50 p-6 rounded-[2.5rem] border border-slate-100 flex flex-col h-[650px]">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <History className="text-slate-400 w-4 h-4" />
                    <h4 className="font-black text-slate-800 uppercase text-xs tracking-widest">
                      Riwayat Terakhir
                    </h4>
                  </div>
                  <button
                    onClick={fetchDailyReports}
                    className="p-1.5 text-indigo-600 hover:bg-white rounded-lg transition-all"
                  >
                    <RefreshCw className={`w-4 h-4 ${fetchingReports ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 no-scrollbar">
                  {dailyReports.map((report) => (
                    <div key={report.id} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm text-xs space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span className="font-black text-slate-800 uppercase">{report.students?.name}</span>
                        <div className="flex items-center gap-1.5">
                          <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase ${
                            report.attendance === 'H' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                          }`}>
                            {report.attendance === 'H' ? 'HADIR' : report.attendance || 'HADIR'}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center text-[9px] font-black text-indigo-600 bg-indigo-50/60 px-2 py-1 rounded-lg">
                        <span>KUIS: {report.quiz_score ?? 100} PTS</span>
                        <span>PEMAHAMAN: {report.comprehension_score}/10</span>
                      </div>
                      <p className="text-[9px] text-slate-400 font-bold uppercase">{formatDateID(report.date)} • {report.subject}</p>
                      <p className="text-[11px] text-slate-600 italic">"{report.notes}"</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'HISTORY_FILTER' ? (
          /* TAB FILTER RIWAYAT PER SISWA & RENTANG TANGGAL DENGAN FITUR CETAK/PDF */
          <div className="space-y-8 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 bg-slate-50 p-6 rounded-3xl border border-slate-100 items-end">
              <div className="space-y-1 md:col-span-2">
                <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                  <GraduationCap className="w-3.5 h-3.5" /> Pilih Siswa
                </label>
                <select
                  className="w-full border-2 border-slate-200 p-3 rounded-2xl text-xs font-black uppercase bg-white text-slate-900 outline-none"
                  value={historyStudentId}
                  onChange={(e) => setHistoryStudentId(e.target.value)}
                >
                  <option value="">-- PILIH SISWA --</option>
                  {students.filter(s => s.is_active !== false).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.branch_id})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" /> Dari Tanggal
                </label>
                <input
                  type="date"
                  className="w-full border-2 border-slate-200 p-3 rounded-2xl text-xs font-black bg-white text-slate-900 outline-none"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-1">
                <label className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" /> Sampai Tanggal
                </label>
                <input
                  type="date"
                  className="w-full border-2 border-slate-200 p-3 rounded-2xl text-xs font-black bg-white text-slate-900 outline-none"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleFilterHistory}
                  disabled={loading || !historyStudentId}
                  className="flex-1 bg-indigo-600 text-white p-3.5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="animate-spin w-4 h-4" /> : <Filter className="w-4 h-4" />}
                  Cari
                </button>
                {filteredHistoryReports.length > 0 && (
                  <button
                    onClick={printHistoryReport}
                    className="bg-slate-900 text-white p-3.5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-black transition-all flex items-center justify-center gap-2"
                    title="Cetak atau Download PDF Riwayat"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* AREA TABEL HASIL FILTER YANG AKAN DICETAK/PDF */}
            <div id="history-print-area" className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
              <table className="w-full text-xs text-slate-800">
                <thead className="bg-slate-50 border-b text-slate-400 font-black uppercase tracking-widest text-[9px]">
                  <tr>
                    <th className="p-4 text-left">Tanggal</th>
                    <th className="p-4 text-left">Mata Pelajaran</th>
                    <th className="p-4 text-center">Absen</th>
                    <th className="p-4 text-center">Nilai Kuis</th>
                    <th className="p-4 text-center">Pemahaman</th>
                    <th className="p-4 text-left">Catatan Tentor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 font-bold">
                  {filteredHistoryReports.map((rep) => (
                    <tr key={rep.id} className="hover:bg-slate-50/50">
                      <td className="p-4 font-mono">{formatDateID(rep.date)}</td>
                      <td className="p-4 uppercase text-indigo-600">{rep.subject}</td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${rep.attendance === 'H' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                          {rep.attendance || 'H'}
                        </span>
                      </td>
                      <td className="p-4 text-center font-black text-emerald-600">{rep.quiz_score ?? 100} PTS</td>
                      <td className="p-4 text-center">{rep.comprehension_score}/10</td>
                      <td className="p-4 italic text-slate-600">"{rep.notes}"</td>
                    </tr>
                  ))}
                  {filteredHistoryReports.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-16 text-center text-slate-400 font-bold uppercase text-[10px]">
                        Silakan pilih siswa dan rentang tanggal, lalu klik tombol "Cari".
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* TAB ANALISIS PERIODIK AI */
          <div className="animate-fade-in space-y-8">
            <div className="flex flex-col md:flex-row items-end gap-6 mb-8 border-b border-slate-50 pb-8">
              <div className="space-y-1.5 flex-1">
                <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest flex items-center gap-2">
                  <GraduationCap className="w-3.5 h-3.5" /> Target Siswa untuk Analisis AI
                </label>
                <select
                  className="w-full border-2 border-slate-200 p-4 rounded-2xl text-xs font-black uppercase bg-white text-slate-900 outline-none cursor-pointer"
                  value={selectedId}
                  onChange={(e) => {
                    setSelectedId(e.target.value);
                    setPeriodicReport(null);
                  }}
                >
                  <option value="">-- CARI NAMA SISWA --</option>
                  {students
                    .filter((s) => s.is_active !== false)
                    .map((s) => (
                      <option key={s.id} value={s.id} className="text-slate-900 font-bold">
                        {s.name} ({s.branch_id})
                      </option>
                    ))}
                </select>
              </div>

              {(isAdmin || isTentor) && (
                <button
                  onClick={handleAnalyzePeriodic}
                  disabled={loading || !selectedId}
                  className="w-full md:w-auto bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl flex items-center justify-center gap-3 hover:bg-indigo-700 transition-all disabled:opacity-50"
                >
                  {loading ? <Loader2 className="animate-spin w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
                  MULAI AI ANALYSIS
                </button>
              )}
            </div>

            {periodicReport ? (
              <div className="space-y-8">
                <div className="flex justify-end gap-3 no-print">
                  <button onClick={printPeriodicReport} className="p-4 bg-slate-900 text-white rounded-2xl hover:bg-black transition-all shadow-lg">
                    <Printer className="w-5 h-5" />
                  </button>
                  <button onClick={() => setPeriodicReport(null)} className="p-4 bg-rose-50 text-rose-400 rounded-2xl hover:bg-rose-600 hover:text-white transition-all">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div id="rapor-periodic-print" className="space-y-8">
                  <div className="p-8 bg-indigo-50/50 rounded-3xl border-2 border-indigo-100 italic text-slate-800 leading-relaxed font-medium text-base text-center">
                    "{periodicReport.summary}"
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-emerald-600 font-black uppercase text-xs">
                        <CheckCircle className="w-4 h-4" /> Pencapaian Utama
                      </div>
                      <ul className="space-y-3">
                        {periodicReport.strengths.map((s, i) => (
                          <li key={i} className="flex items-start gap-3 text-xs font-bold text-slate-600 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
                            <div className="w-2 h-2 bg-emerald-400 rounded-full mt-1 shrink-0" /> {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-rose-500 font-black uppercase text-xs">
                        <AlertCircle className="w-4 h-4" /> Area Perbaikan
                      </div>
                      <ul className="space-y-3">
                        {periodicReport.weaknesses.map((w, i) => (
                          <li key={i} className="flex items-start gap-3 text-xs font-bold text-slate-600 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
                            <div className="w-2 h-2 bg-rose-400 rounded-full mt-1 shrink-0" /> {w}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-24 text-center flex flex-col items-center bg-slate-50 rounded-3xl border-2 border-dashed border-slate-100">
                <Sparkles className="w-10 h-10 text-indigo-200 mb-4" />
                <h4 className="font-black text-lg text-slate-800 uppercase tracking-tight mb-1">
                  Advanced Performance Analytic
                </h4>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                  Pilih siswa di atas untuk memulai analisis Gemini AI
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
